var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useApolloClient } from "@apollo/client";
import globalLogger from "@lazarentertainment/js-logger";
import { createContext, useContext, useEffect, useRef, useSyncExternalStore, } from "react";
import { ChatCursor } from "../chatCursor";
import { createChatFacade, } from "../chatFacade";
const defaultChatContext = {
    pluginState: "initializing",
    initialized: false,
    displayName: null,
    bans: [],
    quotaRefreshSecondsRemaining: 0,
    messages: new Map(),
    reactions: {},
    notifications: new Map(),
    isChannelOwner: false,
    init: () => Promise.resolve(),
    updateDisplayName: () => Promise.resolve({ displayName: "", isValid: false }),
    submitMessage: () => {
        throw new Error("uninitialized context");
    },
    loadMessages: () => Promise.resolve(),
    fetchMessages: () => Promise.resolve([]),
    deleteMessage: () => Promise.resolve(),
    banUser: () => Promise.resolve(),
    unbanUser: () => Promise.resolve(),
    unbanAllUsers: () => Promise.resolve(),
    loadBans: () => Promise.resolve(),
    toggleChatEnabledState: () => Promise.resolve(),
    sendReaction: () => Promise.resolve(),
    deleteReaction: () => Promise.resolve(),
    toggleReaction: () => Promise.resolve(),
    clearState: () => { },
    onChatEvent: () => () => { },
    getLoadedRangeIntersecting: () => undefined,
    apiKey: null,
    loadApiKey: () => Promise.resolve(),
    regenerateApiKey: () => Promise.resolve(),
};
const PublicChatContext = createContext(defaultChatContext);
export const usePublicChatContext = () => useContext(PublicChatContext);
export const PublicChatProvider = (_a) => {
    var { onInit = () => { }, children } = _a, chatConfig = __rest(_a, ["onInit", "children"]);
    const chat = useChat(chatConfig);
    useEffect(() => {
        chat
            .init()
            .then(() => onInit(chat))
            .catch(globalLogger.error);
        return () => chat.close();
    }, []);
    return (_jsx(PublicChatContext.Provider, { value: chat, children: children }));
};
function useChatState(chat, prop, getSnapshot) {
    return useSyncExternalStore((callback) => {
        chat.subscribe(prop, callback);
        return () => chat.unsubscribe(prop, callback);
    }, getSnapshot);
}
function useChat(config) {
    const apolloClient = useApolloClient();
    const chatRef = useRef();
    if (!chatRef.current) {
        chatRef.current = createChatFacade(Object.assign({ apolloClient }, config));
    }
    const chat = chatRef.current;
    const pluginState = useChatState(chat, "pluginState", chat.getPluginState);
    const initialized = useChatState(chat, "initialized", chat.getInitializedState);
    const displayName = useChatState(chat, "displayName", chat.getDisplayName);
    const bans = useChatState(chat, "bans", chat.getBans);
    const quotaRefreshSecondsRemaining = useChatState(chat, "quotaRefreshSecondsRemaining", chat.getQuotaRefreshSecondsRemaining);
    const isChannelOwner = useChatState(chat, "isChannelOwner", chat.getIsChannelOwner);
    const apiKey = useChatState(chat, "apiKey", chat.getApiKey);
    const { reactions, messages } = useSyncExternalStore((callback) => {
        chat.onStateChange(callback);
        return () => chat.offStateChange(callback);
    }, chat.getPubNubClientState);
    const { notifications } = useSyncExternalStore((callback) => {
        chat.onNotificationStateChange(callback);
        return () => chat.offNotificationStateChange(callback);
    }, chat.getPubNubNotificationClientState);
    return Object.assign(Object.assign({}, chat), { pluginState,
        initialized,
        displayName,
        messages,
        reactions,
        notifications,
        bans,
        quotaRefreshSecondsRemaining,
        isChannelOwner,
        apiKey });
}
/**
 * Use a cursor when chat is in 'replay' mode. To use this effectively,
 * e.g. in a VOD context, do the following:
 *  - Call `setCursor` when you get a video timestamp update
 *  - Restrict messages visible in the UI to those with
 *    timetoken <= cursor
 */
export function useChatCursor(config = {}) {
    const chat = usePublicChatContext();
    const cursorRef = useRef();
    if (!cursorRef.current) {
        cursorRef.current = new ChatCursor(chat, config);
    }
    const cursor = cursorRef.current;
    const cursorTimetoken = useSyncExternalStore((callback) => {
        cursor.onUpdate(callback);
        return () => cursor.offUpdate();
    }, cursor.getCursorTimetoken);
    return {
        cursor: cursorTimetoken,
        setCursor: cursor.updateCursor,
    };
}
